import { ConnectedPosition } from '@angular/cdk/overlay'
import { afterNextRender, Component, DoCheck, ElementRef, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ErrorStateMatcher } from '@angular/material/core'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { TypeaheadToimittaja } from '@lemon/core-firebase/model/lasku'
import { WebTositeUusiToimittaja } from '@lemon/core-firebase/model/web-tosite-api'
import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { of, Subject } from 'rxjs'
import { filter, switchMap, takeUntil } from 'rxjs/operators'
import { KayttajaService } from '../../../_angular/service/kayttaja.service'
import { LaskuService } from '../../../_angular/service/lasku/lasku.service'

export interface UusiToimittajaLomake {
  nimi: FormControl<string>
  ytunnus: FormControl<string>
}

export const tunnisteValidator = (tunnisteService: ToimittajaTunnisteService): ValidatorFn => {
  return (control: AbstractControl<string>): ValidationErrors | null => {
    const arvo = (control.value ?? '').trim()
    if (!arvo) {
      return null
    }
    const tyyppi = tunnisteService.luokitteleTunniste(arvo)?.tyyppi
    return tyyppi === 'y-tunnus' || tyyppi === 'vat' ? null : { tunniste: true }
  }
}

/** Tallennuksessa luotavan toimittajan lomake. Disabloituna ei odottavaa toimittajaa eikä vaikutusta validiteettiin. */
export const luoUusiToimittajaLomake = (tunnisteService: ToimittajaTunnisteService): FormGroup<UusiToimittajaLomake> => {
  const lomake = new FormGroup<UusiToimittajaLomake>({
    nimi: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    ytunnus: new FormControl<string>('', { nonNullable: true, validators: [tunnisteValidator(tunnisteService)] }),
  })
  lomake.disable()
  return lomake
}

export const toimittajaValittuValidator: ValidatorFn = (lomake: AbstractControl): ValidationErrors | null => {
  const toimittajaAvain = lomake.get('toimittajaAvain')
  const uusiToimittaja = lomake.get('uusiToimittaja')
  return toimittajaAvain?.value || uusiToimittaja?.enabled ? null : { toimittajaPuuttuu: true }
}

export const uusiToimittajaLomakkeesta = (lomake: FormGroup<UusiToimittajaLomake>): WebTositeUusiToimittaja | null => {
  if (lomake.disabled) {
    return null
  }
  const ytunnus = lomake.controls.ytunnus.value.trim()
  return { nimi: lomake.controls.nimi.value.trim(), tunnisteet: ytunnus ? [ytunnus] : [] }
}

interface Toimittajavaihtoehto {
  avain: string
  nimi: string
  ytunnus: string
}

const LUO_UUSI_INDEKSI = -1
let seuraavaKomponentinNumero = 0

@Component({
  selector: 'app-toimittaja-kentta',
  templateUrl: './toimittaja-kentta.component.html',
  styleUrls: ['./toimittaja-kentta.component.css'],
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ToimittajaKenttaComponent implements OnInit, DoCheck, OnDestroy {
  private _ngUnsubscribe = new Subject<void>()
  private _kaikkiToimittajat: TypeaheadToimittaja[] = []
  private _idEtuliite = `toimittaja-kentta-${seuraavaKomponentinNumero++}`

  @Input() valittuNimi = ''
  @Input() infoteksti = ''
  @Input() virhe = ''
  @Input() pakollinen = true
  @Input() tekoalynEhdotus = false
  @Input() uusiToimittajaLomake: FormGroup<UusiToimittajaLomake>
  @Output() valittuNimiChange = new EventEmitter<string>()
  @Output() valittuAvainChange = new EventEmitter<string | null>()

  @ViewChild('kentta', { static: true }) private _kentta: ElementRef<HTMLInputElement>
  @ViewChild('kentta', { static: true, read: MatInput }) private _kentanMatInput: MatInput
  @ViewChild('kenttaAlue', { static: true }) private _kenttaAlue: ElementRef<HTMLElement>
  @ViewChild('hakukentta') private _hakukentta: ElementRef<HTMLInputElement>

  constructor(
    private _kayttajaService: KayttajaService,
    private _laskuService: LaskuService,
    private _elementRef: ElementRef<HTMLElement>,
    private _injector: Injector,
    private _toimittajaTunnisteService: ToimittajaTunnisteService,
  ) {}

  pudotusvalikkoAuki = false
  hakuteksti = ''
  aktiivinenIndeksi = LUO_UUSI_INDEKSI
  paneelinLeveys = 0

  readonly luoUusiIndeksi = LUO_UUSI_INDEKSI
  readonly paneelinSijainnit: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: 4 },
  ]

  readonly vaihtoehdot = signal<Toimittajavaihtoehto[]>([])

  toimittajaVirheTila: ErrorStateMatcher = {
    isErrorState: () => !!this.virhe,
  }

  valitonVirheTila: ErrorStateMatcher = {
    isErrorState: control => !!control?.invalid,
  }

  ngOnInit() {
    this._kayttajaService.kayttajanTiedotObservable
      .pipe(
        switchMap(kayttaja => (kayttaja ? this._laskuService.getTypeaheadToimittajat(kayttaja) : of<TypeaheadToimittaja[]>([]))),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(toimittajat => {
        this._kaikkiToimittajat = toimittajat.filter(t => !t.poistettu).sort((a, b) => a.nimi.localeCompare(b.nimi, 'fi'))
        this._suodataVaihtoehdot()
      })

    this.uusiToimittajaLomake.controls.nimi.valueChanges
      .pipe(
        filter(() => this.uusiToimittajaLomake.enabled),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(nimi => {
        this.valittuNimi = nimi
        this.valittuNimiChange.emit(nimi)
      })
  }

  ngDoCheck() {
    // MatInput päivittää virhetilansa itse vain, kun kenttään on sidottu lomakekontrolli; valitsinkentällä sitä ei ole
    this._kentanMatInput.updateErrorState()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  get uusiNimi(): FormControl<string> {
    return this.uusiToimittajaLomake.controls.nimi
  }

  get uusiYtunnus(): FormControl<string> {
    return this.uusiToimittajaLomake.controls.ytunnus
  }

  annaVaihtoehdonId(indeksi: number) {
    return indeksi === LUO_UUSI_INDEKSI ? `${this._idEtuliite}-luo-uusi` : `${this._idEtuliite}-vaihtoehto-${indeksi}`
  }

  vaihdaTila() {
    if (this.pudotusvalikkoAuki) {
      this.suljePudotusvalikko()
    } else {
      this.avaaPudotusvalikko()
    }
  }

  avaaPudotusvalikko(alkuteksti = '') {
    if (!this.pudotusvalikkoAuki) {
      this.paneelinLeveys = this._kenttaAlue.nativeElement.getBoundingClientRect().width
      this.pudotusvalikkoAuki = true
    }
    this.paivitaHaku(alkuteksti)
    if (!alkuteksti) {
      const valittuIndeksi = this.vaihtoehdot().findIndex(v => v.nimi === this.valittuNimi)
      this.aktiivinenIndeksi = valittuIndeksi >= 0 ? valittuIndeksi : LUO_UUSI_INDEKSI
    }
    this._rullaaAktiiviseen()
    afterNextRender(
      () => {
        this._hakukentta?.nativeElement.focus()
        this._hakukentta?.nativeElement.setSelectionRange(alkuteksti.length, alkuteksti.length)
      },
      { injector: this._injector },
    )
  }

  tyhjennaHaku() {
    this.paivitaHaku('')
    this._hakukentta?.nativeElement.focus()
  }

  suljePudotusvalikko(palautaFokus = false) {
    this.pudotusvalikkoAuki = false
    this.hakuteksti = ''
    this._suodataVaihtoehdot()
    if (palautaFokus) {
      this._kentta.nativeElement.focus()
    }
  }

  kasitteleUlkopuolinenKlikkaus(event: MouseEvent) {
    // Kentän oma klikkaus avaa ja sulkee valikon; ilman tätä ulkopuolisen klikin sulkeminen avaisi sen heti uudelleen
    if (event.composedPath().includes(this._kenttaAlue.nativeElement)) {
      return
    }
    this.suljePudotusvalikko()
  }

  estaFokuksenSiirto(event: MouseEvent) {
    if (!(event.target instanceof HTMLInputElement)) {
      event.preventDefault()
    }
  }

  kasitteleKentanNappain(event: KeyboardEvent) {
    if (this._onKirjoitettavaMerkki(event)) {
      event.preventDefault()
      this.avaaPudotusvalikko(event.key)
      return
    }
    if (!this.pudotusvalikkoAuki && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault()
      this.avaaPudotusvalikko()
    }
  }

  kasitteleHaunNappain(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.aktiivinenIndeksi = Math.min(this.aktiivinenIndeksi + 1, this.vaihtoehdot().length - 1)
        this._rullaaAktiiviseen()
        break
      case 'ArrowUp':
        event.preventDefault()
        this.aktiivinenIndeksi = Math.max(this.aktiivinenIndeksi - 1, LUO_UUSI_INDEKSI)
        this._rullaaAktiiviseen()
        break
      case 'Enter':
        event.preventDefault()
        this._valitseAktiivinen()
        break
      case 'Escape':
        event.preventDefault()
        this.suljePudotusvalikko(true)
        break
      case 'Tab':
        this.suljePudotusvalikko(true)
        break
    }
  }

  private _onKirjoitettavaMerkki(event: KeyboardEvent) {
    return event.key.length === 1 && event.key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey
  }

  private _valitseAktiivinen() {
    if (this.aktiivinenIndeksi === LUO_UUSI_INDEKSI) {
      this.luoUusiToimittaja()
      return
    }
    const vaihtoehto = this.vaihtoehdot()[this.aktiivinenIndeksi]
    if (vaihtoehto) {
      this.valitseToimittaja(vaihtoehto)
    }
  }

  private _rullaaAktiiviseen() {
    afterNextRender(
      () => {
        document.getElementById(this.annaVaihtoehdonId(this.aktiivinenIndeksi))?.scrollIntoView({ block: 'nearest' })
      },
      { injector: this._injector },
    )
  }

  private _poistaUusiToimittaja() {
    if (this.uusiToimittajaLomake.disabled) {
      return
    }
    this.uusiToimittajaLomake.reset({ nimi: '', ytunnus: '' }, { emitEvent: false })
    this.uusiToimittajaLomake.disable()
  }

  valitseToimittaja(v: Toimittajavaihtoehto) {
    this._poistaUusiToimittaja()
    this.valittuNimi = v.nimi
    this.valittuNimiChange.emit(v.nimi)
    this.valittuAvainChange.emit(v.avain)
    this.suljePudotusvalikko(true)
  }

  luoUusiToimittaja() {
    const nimi = this.hakuteksti.trim()
    this.suljePudotusvalikko()
    this.valittuAvainChange.emit(null)
    this.uusiToimittajaLomake.enable({ emitEvent: false })
    this.uusiToimittajaLomake.setValue({ nimi, ytunnus: '' })
    afterNextRender(
      () => {
        this._elementRef.nativeElement.querySelector<HTMLInputElement>('.uusi-toimittaja input')?.focus()
      },
      { injector: this._injector },
    )
  }

  peruutaUusiToimittaja() {
    this._poistaUusiToimittaja()
    this.valittuNimi = ''
    this.valittuNimiChange.emit('')
    this.valittuAvainChange.emit(null)
    this._kentta.nativeElement.focus()
  }

  paivitaHaku(arvo: string) {
    this.hakuteksti = arvo
    this._suodataVaihtoehdot()
    this.aktiivinenIndeksi = this.hakuteksti.trim() && this.vaihtoehdot().length > 0 ? 0 : LUO_UUSI_INDEKSI
  }

  private _suodataVaihtoehdot() {
    const hakusana = this.hakuteksti.trim().toLowerCase()
    const suodatetutToimittajat =
      hakusana.length === 0
        ? this._kaikkiToimittajat
        : this._kaikkiToimittajat.filter(
            t =>
              t.nimi.toLowerCase().includes(hakusana) ||
              t.ytunnus?.toLowerCase().includes(hakusana) ||
              this._toimittajaTunnisteService.naytettavaTunnisteArvosta(t.ytunnus)?.toLowerCase().includes(hakusana),
          )

    this.vaihtoehdot.set(
      suodatetutToimittajat.slice(0, 50).map(t => ({
        avain: t.avain,
        nimi: t.nimi,
        ytunnus: this._toimittajaTunnisteService.naytettavaTunnisteArvosta(t.ytunnus),
      })),
    )
  }
}
